import { memo, useRef } from 'react';
import { Bold, Code2, Image, Italic, Mic, Paperclip, Send, Smile, Sticker } from 'lucide-react';
import { IconButton } from '@cyphex/ui';
import { useMessagingPolicy } from '@/hooks/useMyPolicy';
import type { GifItem, StickerItem } from '../../types';
import { EmojiPicker } from './EmojiPicker';
import { GifPicker, StickerPicker } from './GifStickerPicker';
import { ScheduleMenu } from './ScheduleMenu';

interface ComposerToolbarProps {
  canSend: boolean;
  /**
   * The HOST's veto on voice messages, on top of the admin policy below. False
   * only where a second microphone capture would fight one already running —
   * see `ComposerProps.voiceMessages`.
   */
  voiceMessages: boolean;
  onWrap: (prefix: string, suffix?: string) => void;
  onEmoji: (emoji: string) => void;
  onGif: (gif: GifItem) => void;
  onSticker: (sticker: StickerItem) => void;
  onFiles: (files: readonly File[]) => void;
  onRecord: () => void;
  onSchedule: (isoDateTime: string) => void;
  onSend: () => void;
}

/**
 * The row of composer affordances, matching the reference screen's icon set.
 *
 * Three of them are governed by the ADMIN MESSAGING POLICY — attachments, voice
 * messages and schedule-send. A workspace with attachments switched off gets no
 * paper clip, rather than a paper clip that opens a file picker and then a 403
 * from a send the API was always going to refuse. The API is still the boundary
 * (apps/api/src/routes/messages.ts); this is the courtesy.
 */
export const ComposerToolbar = memo(function ComposerToolbar({
  canSend,
  voiceMessages,
  onWrap,
  onEmoji,
  onGif,
  onSticker,
  onFiles,
  onRecord,
  onSchedule,
  onSend,
}: ComposerToolbarProps) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const { allowFileAttachments, allowVoiceMessages, allowScheduledSend } = useMessagingPolicy();

  return (
    /* WRAPS because this same composer is now mounted in a 320px rail (the
       in-call chat panel): nine 40px buttons in one non-wrapping row overflowed
       it. At page width there is room for the row and nothing moves. */
    <div className="flex flex-wrap items-center gap-0.5 px-2 pb-2">
      <IconButton icon={Bold} label="Bold" size="xl" variant="ghost" onClick={() => onWrap('**')} />
      <IconButton icon={Italic} label="Italic" size="xl" variant="ghost" onClick={() => onWrap('_')} />
      <IconButton
        icon={Code2}
        label="Code block"
        size="xl"
        variant="ghost"
        onClick={() => onWrap('\n```\n', '\n```\n')}
      />

      <span className="mx-1 h-5 w-px bg-border" aria-hidden />

      {allowFileAttachments ? (
        <>
          <IconButton
            icon={Paperclip}
            label="Attach a file"
            size="xl"
            variant="ghost"
            onClick={() => fileInput.current?.click()}
          />
          <input
            ref={fileInput}
            type="file"
            multiple
            className="sr-only"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              if (files.length > 0) onFiles(files);
              event.target.value = '';
            }}
          />
        </>
      ) : null}

      <EmojiPicker
        onSelect={onEmoji}
        trigger={<IconButton icon={Smile} label="Emoji" size="xl" variant="ghost" />}
      />
      <StickerPicker
        onSelect={onSticker}
        trigger={<IconButton icon={Sticker} label="Stickers" size="xl" variant="ghost" />}
      />
      <GifPicker
        onSelect={onGif}
        trigger={<IconButton icon={Image} label="GIFs" size="xl" variant="ghost" />}
      />
      {allowVoiceMessages && voiceMessages ? (
        <IconButton icon={Mic} label="Record a voice message" size="xl" variant="ghost" onClick={onRecord} />
      ) : null}

      <span className="flex-1" />

      {allowScheduledSend ? <ScheduleMenu onSchedule={onSchedule} disabled={!canSend} /> : null}
      <IconButton
        icon={Send}
        label="Send message"
        size="xl"
        variant="primary"
        disabled={!canSend}
        onClick={onSend}
      />
    </div>
  );
});
